// SFX catalog — admin-uploaded audio clips bound to named slots.
//
// The Admin Designer can upload audio files of kind="sfx" and bind
// each one to a slot (one of SFX_SLOTS — "round_start", "bag_in_hole",
// etc.) via the asset's `slug` column. This module fetches the active
// catalog on first import and indexes it by slot so feedback.ts can
// look up an audio URL synchronously when it needs to play.
//
// Why module-level state instead of a React hook: feedback.ts is plain
// TS called from imperative game loops (handleThrow, ghost AI tick) —
// not React components. Module state survives across renders and
// reads in O(1).
//
// Fallback: if no asset is bound to a slot (or sound is disabled, or
// the audio fails to load), `playNamedSfx` calls the provided
// synthesized fallback. That keeps the game audible out-of-the-box
// before any uploads exist, and resilient when an upload 404s.

import type { SfxSlot } from "@/lib/queries";
import { isSoundEnabled } from "@/lib/feedback";

type CatalogEntry = {
  url: string;
  gain: number;
};

// slot → POOL of entries. Multiple sfx assets sharing a slug form a
// pool; each play picks one at random for variety. Empty until
// loadCatalog resolves.
const catalog = new Map<SfxSlot, CatalogEntry[]>();
let loadPromise: Promise<void> | null = null;
let loadedAt = 0;

// Preloaded HTMLAudioElement per CLIP URL (not per slot, since a slot
// can hold several). One master per url, kept warm so the network +
// decode happens once; each play clones it so overlapping fires don't
// cut each other off.
const masters = new Map<string, HTMLAudioElement>();

type AssetRow = {
  kind: string;
  slug: string | null;
  status: string;
  imageUrl: string | null;
  metadata?: { gain?: number } | null;
};

async function fetchCatalog(): Promise<void> {
  try {
    const r = await fetch("/api/assets?kind=sfx", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!r.ok) return;
    const rows = (await r.json()) as AssetRow[];
    catalog.clear();
    masters.clear();
    // Server filters to active. EVERY active clip with a slug joins
    // that slot's pool — no first-wins dedupe anymore.
    for (const row of rows) {
      if (row.kind !== "sfx") continue;
      if (row.status !== "active") continue;
      if (!row.slug || !row.imageUrl) continue;
      const gain =
        typeof row.metadata?.gain === "number" &&
        Number.isFinite(row.metadata.gain)
          ? Math.max(0, Math.min(1, row.metadata.gain))
          : 1;
      const slot = row.slug as SfxSlot;
      const pool = catalog.get(slot) ?? [];
      pool.push({ url: row.imageUrl, gain });
      catalog.set(slot, pool);
    }
    // Best-effort preload one master Audio per unique clip url so the
    // first play is gapless. play() buffer-and-starts if not ready.
    for (const pool of catalog.values()) {
      for (const entry of pool) {
        if (masters.has(entry.url)) continue;
        try {
          const a = new Audio(entry.url);
          a.preload = "auto";
          a.volume = entry.gain;
          masters.set(entry.url, a);
        } catch {
          // Ignore audio construction failures (e.g. invalid URL).
        }
      }
    }
    loadedAt = Date.now();
  } catch {
    // Network failure — leave catalog empty so fallbacks run.
  }
}

// Public: load (or skip if already loaded). Idempotent.
export function loadSfxCatalog(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = fetchCatalog();
  return loadPromise;
}

// Public: drop the cached catalog so the next play (or explicit
// loadSfxCatalog) re-fetches. Useful after Admin Designer changes.
export function invalidateSfxCatalog(): void {
  catalog.clear();
  masters.clear();
  loadPromise = null;
  loadedAt = 0;
}

// Stale-while-revalidate hint — kicks a background refresh if the
// catalog is older than this. Keeps in-game playback snappy while
// new admin uploads still surface within a few minutes.
const SWR_AGE_MS = 5 * 60_000;

// Play the audio bound to `slot`. If nothing is bound (or sound is
// disabled, or playback errors), the `fallback` function runs
// instead — typically the synthesized Web Audio version in
// feedback.ts. fallback is REQUIRED so callers can't accidentally
// no-op.
export function playNamedSfx(slot: SfxSlot, fallback: () => void): void {
  if (!isSoundEnabled()) {
    // Sound disabled wholesale; both paths are silent.
    return;
  }

  // Trigger a background load on the very first call so we have a
  // catalog ready for subsequent plays. The first call always falls
  // back since the fetch hasn't resolved yet — acceptable since the
  // synthesized version sounds fine.
  if (!loadPromise) {
    void loadSfxCatalog();
  } else if (Date.now() - loadedAt > SWR_AGE_MS) {
    // Refresh stale catalog in the background. Doesn't block this
    // play call — the previous URL is still playable.
    loadPromise = fetchCatalog();
  }

  // Pick a random clip from the slot's pool — variety per fire. Empty
  // pool → synth fallback.
  const pool = catalog.get(slot);
  if (!pool || pool.length === 0) {
    fallback();
    return;
  }
  const entry = pool[Math.floor(Math.random() * pool.length)]!;
  const master = masters.get(entry.url);
  if (!master) {
    fallback();
    return;
  }

  // Clone so overlapping fires don't cut each other off (a bag
  // landing in the hole right as the round-start fanfare ends, etc.).
  let instance: HTMLAudioElement;
  try {
    instance = master.cloneNode(true) as HTMLAudioElement;
    instance.volume = entry.gain;
  } catch {
    fallback();
    return;
  }

  const promise = instance.play();
  if (promise && typeof promise.catch === "function") {
    promise.catch(() => {
      // Autoplay can be blocked if the audio context wasn't unlocked
      // by a user gesture yet. Synthesized fallback runs through the
      // Web Audio context which DOES auto-resume on first interaction,
      // so it's the more reliable path in that case.
      fallback();
    });
  }
}

// Eager preload — call once on game mount so the round-start /
// throw-release fires aren't waiting on a network round-trip.
export function preloadSfxCatalog(): void {
  void loadSfxCatalog();
}
